/**
 * NAMED FAILURE STATE 1 — THE BROWSER REFUSED THE POPUP.
 *
 * IT IS A STATE, NOT A TOAST. A toast is gone in four seconds and takes the only instruction with
 * it; this failure needs the person to do something in their browser chrome and then come back, so
 * the explanation has to still be there when they do.
 *
 * IT NEVER REACHES OUR API. `signInWithPopup` throws before any request to `authors-api` exists,
 * so there is no HTTP status and no `error_code` to show — and the corpus attaches none, correctly
 * (adr/0007-web-app-stack.md; docs/design/09-authors-app.md).
 *
 * THE REMEDY IS A SECOND EXPLICIT CLICK, and that is not a shrug. A `window.open` that is a direct
 * response to a user gesture is the thing browsers allow; the first attempt may have been refused
 * because it was not close enough to one, or because a blanket blocker is on. So the same button is
 * offered again, prominently, rather than replaced with an apology.
 *
 * AND THERE IS STILL NO REDIRECT BUTTON. The fallback needs four prerequisites, only one of which
 * is a file of ours; the build plan accepts shipping popup-only until they are done
 * (docs/design/12-build-plan.md). Offering redirect early would not be a fallback, it would
 * be a second way to fail in every current browser. The disclosure below says so, so the degraded
 * path is visible rather than merely absent.
 *
 * ★ IN PLAIN WORDS (the owner's live look at real cabinets, 2026-10-01 / design 15: "do we really
 * need to show internal tech info ... to author - its like a user for us"). The disclosure used to
 * list the four prerequisites by their technical names - a sign-in path on the cabinet's address,
 * the app pointed at it, the address allowed by Firebase, Apple and Google told where to send
 * people back, plus a separate Apple registration - each marked "ours to do". To an author that is
 * our to-do list, not an answer. It now says what is true for them: there is a second way, it is
 * switched off until it works in every browser, and the pop-up is the way in until then. The list
 * itself stays where it is kept, ../firebase/redirect-fallback.ts. And the hand-off's address is no
 * longer printed: "/enter" is a path, not something an author types.
 */
import { Alert, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import { REDIRECT_FALLBACK_AVAILABLE } from '../firebase/redirect-fallback';

export interface PopupBlockedPanelProps {
  /** The provider buttons again, so the second click is one press away. */
  readonly retry: ReactNode;
}

export function PopupBlockedPanel({ retry }: PopupBlockedPanelProps) {
  const { t } = useTranslation('auth');
  return (
    <Alert role="alert" variant="light" color="yellow" title={t('popupBlocked.title')}>
      <Stack gap="md">
        <Text size="sm">{t('popupBlocked.allowPopups')}</Text>

        {retry}

        <Text size="sm">{t('popupBlocked.fromTheApp')}</Text>

        {REDIRECT_FALLBACK_AVAILABLE ? null : (
          <details>
            <summary>
              <Text component="span" size="sm">
                {t('popupBlocked.whyNoOtherWay')}
              </Text>
            </summary>
            <Text size="sm" c="dimmed" pt="xs">
              {t('popupBlocked.secondWay')}
            </Text>
          </details>
        )}
      </Stack>
    </Alert>
  );
}
